import {
  ClipboardList,
  LayoutDashboard,
  MapPin,
  ShieldCheck,
  Smartphone,
  Wallet,
} from "lucide-react";
import PartnerPage from "../partner/PartnerPage";
import { DashBoardLink } from "../../Helper/config";
import merchantStripImg from "../../assets/merchant-strip.webp";
import cookImg from "../../assets/seller-cook.jpg";
import homeKitchenImg from "../../assets/home-kitchen.jpg";
import oppCooksImg from "../../assets/merchant-opp-cooks.webp";
import oppPartnerImg from "../../assets/merchant-opp-partner.webp";
import oppWordImg from "../../assets/merchant-opp-word.webp";

const LOGIN_URL = `${DashBoardLink}/login`;

/**
 * A merchant does not cook. They bring home chefs onto OwnFood and then run
 * those kitchens from the merchant dashboard, which is exactly what the
 * merchant module supports: an NID-backed application, phone-OTP seller
 * onboarding, and a list of the kitchens that merchant created.
 */
const content = {
  hero: {
    title: "Bring new cooks, earn real income",
    subtitle:
      "You never have to cook. Find home chefs in your area, sign them up on OwnFood, and manage their kitchens from one dashboard.",
    cta: { label: "Apply as a merchant", href: LOGIN_URL },
  },

  strip: {
    img: merchantStripImg,
    alt: "A merchant holding the commission he earned onboarding home cooks",
  },

  why: {
    heading: "Why become a merchant?",
    sub: "Bangladesh is full of people who cook brilliantly and have no way to sell. You connect the two sides.",
    items: [
      {
        icon: Wallet,
        title: "Earn per chef you bring on",
        desc: "৳100 for every home chef you sign up who gets approved, a one-time bounty with no cap on how many you onboard.",
      },
      {
        icon: LayoutDashboard,
        title: "One dashboard for every kitchen",
        desc: "Every kitchen you onboard stays in your merchant dashboard, so you can see them all and keep their profiles up to date in one place.",
      },
      {
        icon: ShieldCheck,
        title: "No kitchen, no cooking",
        desc: "You do not need a kitchen, a licence, or a single recipe. Your work is finding chefs and getting them set up properly.",
      },
    ],
  },

  gettingStarted: {
    img: cookImg,
    alt: "A home chef cooking in her kitchen",
    heading: "Getting started",
    body: "Apply from the OwnFood dashboard with your address and NID. Our team reviews it and, once you are approved, the merchant tools unlock: add a seller, see every kitchen you onboarded, and update their details when something changes.",
    cta: { label: "Start your application", href: LOGIN_URL },
  },

  steps: {
    heading: "How it works",
    sub: "From application to your first onboarded kitchen.",
    items: [
      {
        title: "Create a buyer account",
        desc: "Register on ownfood.com. Merchant access is added to your existing OwnFood account.",
        link: {
          label: "ownfood.com/authentication/register",
          href: "https://ownfood.com/authentication/register",
        },
      },
      {
        title: "Apply from the dashboard",
        desc: "Log in to dashboard.ownfood.com and go to Application → Become a Merchant.",
        link: {
          label: "dashboard.ownfood.com/login",
          href: LOGIN_URL,
        },
      },
      {
        title: "Get verified",
        desc: "Submit your address and NID photos. Our team reviews the application and tells you if anything needs to be resubmitted.",
      },
      {
        title: "Onboard a home chef",
        desc: "Use Add Seller, enter the chef's phone number, and confirm the code sent to their phone. Their consent is part of the flow.",
      },
      {
        title: "Set up and manage the kitchen",
        desc: "Fill in the chef's owner details and kitchen profile. The kitchen then appears under All Sellers for you to maintain.",
      },
    ],
  },

  checklist: {
    heading: "What to keep ready",
    sub: "Two short lists: what the merchant application asks of you, and what you collect from each chef you onboard.",
    groups: [
      {
        icon: ClipboardList,
        title: "For your application",
        items: [
          "Your full address",
          "Your NID number",
          "A photo of your NID, front",
          "A photo of your NID, back",
          "The phone number and email on your OwnFood account",
        ],
      },
      {
        icon: Smartphone,
        title: "From the chef, to onboard them",
        items: [
          "A phone number they can receive an SMS code on",
          "Their name, date of birth, and NID details",
          "A profile photo and their NID photos",
          "Kitchen name, logo, and speciality",
        ],
      },
      {
        icon: MapPin,
        title: "About their kitchen",
        items: [
          "Full pickup address",
          "Day shift pickup instructions",
          "Night shift pickup instructions",
          "Cuisines and dietary restrictions they can handle",
        ],
      },
    ],
  },

  band: {
    kicker: "Earnings",
    heading: "৳100 for every approved chef, no cap",
    body: "The bounty is one-time per chef and paid after their kitchen is approved. Onboard five chefs in a month or fifty, the rate does not change.",
    bullets: [
      "One-time ৳100 per home chef you onboard who gets approved",
      "No limit on how many kitchens you bring on",
      "Nothing to pay to apply or to stay a merchant",
      "Track every kitchen you onboarded from your merchant dashboard",
    ],
    img: homeKitchenImg,
    alt: "A home kitchen ready for service",
    cta: { label: "Apply as a merchant", href: LOGIN_URL },
  },

  cards: {
    heading: "More ways to earn, more ways to help",
    sub: "Three jobs, all of them off the stove.",
    items: [
      {
        img: oppCooksImg,
        alt: "Two home cooks talking over a dish in a home kitchen",
        title: "Bring in home cooks",
        desc: "Introduce cooks from your community, network, or events.",
      },
      {
        img: oppPartnerImg,
        alt: "A long communal table of people sharing a meal",
        title: "Partner with local groups",
        desc: "Work with communities and organisations to onboard cooks.",
      },
      {
        img: oppWordImg,
        alt: "Friends talking around a table",
        title: "Spread the word",
        desc: "Share OwnFood at events, online, or through your network.",
      },
    ],
  },

  faq: {
    heading: "FAQ",
    items: [
      {
        q: "What is a merchant on OwnFood?",
        a: "A merchant brings home chefs onto the platform and manages their kitchens from the merchant dashboard. You are not a seller and you do not cook. Your work is building and looking after a network of kitchens.",
      },
      {
        q: "What does it cost to become a merchant?",
        a: "Nothing. The application is free and there is no monthly fee.",
      },
      {
        q: "How much do I earn?",
        a: "৳100 for every home chef you onboard who gets approved. It is a one-time bounty per chef and there is no cap on how many you bring on.",
      },
      {
        q: "Do I need prior experience?",
        a: "No. If you know people who cook well and want to sell, that is enough. The dashboard walks you through each onboarding step.",
      },
      {
        q: "What do I need to apply?",
        a: "Your address, your NID number, and clear photos of both sides of your NID. You apply from the dashboard with your existing OwnFood account.",
      },
      {
        q: "How long does approval take?",
        a: "Our team reviews each application manually. An application can be approved, rejected, or sent back for resubmission. You are told which, and why, so you can fix it.",
      },
      {
        q: "Can I onboard a chef without them knowing?",
        a: "No. Onboarding sends a one-time code to the chef's own phone number and you cannot continue until that code is confirmed. The chef always consents.",
      },
      {
        q: "What can I do with a kitchen I onboarded?",
        a: "You can see it in your dashboard and keep its owner and kitchen details current. Merchants only have access to the kitchens they created themselves.",
      },
    ],
  },

  contact: {
    heading: "Want to talk it through first?",
    body: "Tell us which area you cover and how many chefs you already know. We will get back to you.",
    cta: { label: "Contact us", href: "/contact-us" },
  },
};

export default function BecomeMerchant() {
  return <PartnerPage content={content} />;
}
